export const useTokenManager = () => {
  const tokenCheckInterval = ref<ReturnType<typeof setInterval> | null>(null);

  // Vérification de la configuration avant tout appel réseau
  const isNetworkEnabled = (): boolean => {
    const config = useRuntimeConfig();

    // En mode développement sans API configurée, désactiver complètement le réseau
    if (process.dev && !config.public.apiBaseUrl) {
      return false;
    }

    // Vérifier que l'API est configurée
    if (!config.public.apiBaseUrl || !config.public.djApiClient || !config.public.djApiKey) {
      return false;
    }

    return true;
  };

  // Méthode pour décoder un JWT sans vérification de signature
  const decodeJWT = (token: string | null): any | null => {
    try {
      if (!token) return null;

      // Supprimer le préfixe "Bearer " s'il existe
      const cleanToken = token.replace(/^Bearer\s+/, '');

      // Décoder le payload (partie du milieu du JWT)
      const base64Url = cleanToken.split('.')[1];
      if (!base64Url) return null;

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error('❌ Erreur lors du décodage du JWT:', error);
      return null;
    }
  };

  // Méthode pour vérifier si le token est expiré
  const isTokenExpired = (token: string | null): boolean => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
      console.warn("⚠️ Token invalide ou sans date d'expiration");
      return true;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    const isExpired = decoded.exp < currentTime;

    if (isExpired) {
      console.log('⏰ Token expiré:', {
        exp: new Date(decoded.exp * 1000),
        now: new Date(currentTime * 1000),
      });
    }

    return isExpired;
  };

  // Méthode pour vérifier et rafraîchir le token si nécessaire
  const ensureValidToken = async (): Promise<string | null> => {
    try {
      // Si réseau désactivé, retourner un token guest immédiatement
      if (!isNetworkEnabled()) {
        console.log('🔧 Réseau désactivé - Token guest retourné');
        return null;
      }

      // Utiliser les cookies Nuxt 3 comme dans le codebase existant
      const jwtCookie = useCookie<string | null>('token');
      const refreshCookie = useCookie<string | null>('refreshToken');

      const currentToken = jwtCookie.value;
      const config = useRuntimeConfig();

      // Mode développement : si pas de token et pas d'API configurée, autoriser le mode guest
      if (!currentToken) {
        return null;
      }

      // Vérifier si le token est expiré ou expire dans les 5 prochaines minutes
      const decoded = decodeJWT(currentToken);
      if (!decoded || !decoded.exp) {
        console.warn('⚠️ Token invalide, mais continuer en mode dégradé');
        return currentToken;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = decoded.exp - currentTime;

      // Si le token expire dans moins de 2 minutes (120 secondes), essayer de le rafraîchir
      if (timeUntilExpiry < 290) {
        // Essayer de rafraîchir le token seulement si l'API de refresh est disponible ET réseau activé
        if (refreshCookie.value && config.public.apiBaseUrl && isNetworkEnabled()) {
          try {
            const response = await fetch('/api/auth/refresh', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ refreshToken: refreshCookie.value }),
              // Timeout géré manuellement pour compatibilité
              signal: (() => {
                const controller = new AbortController();
                setTimeout(() => controller.abort(), 5000);
                return controller.signal;
              })(),
            });

            if (!response.ok) {
              throw new Error(`HTTP ${response.status}`);
            }

            const data = (await response.json()) as {
              isAuthenticated: boolean;
              token: string;
              refreshToken: string;
            };

            if (data?.isAuthenticated && data.token && data.refreshToken) {
              jwtCookie.value = data.token;
              refreshCookie.value = data.refreshToken;
              return data.token;
            }
          } catch (error: any) {
            console.warn(
              "⚠️ Impossible de rafraîchir le token, continuons avec l'ancien:",
              error?.message || error
            );
          }
        } else {
          console.log('🔧 API de refresh non disponible en mode développement');
        }

        // Si le rafraîchissement échoue, continuer avec le token actuel en mode dégradé
        console.log('⚠️ Utilisation du token expirant en mode dégradé');
        return currentToken;
      }

      console.log('✅ Token valide, expire dans', timeUntilExpiry, 'secondes');
      return currentToken;
    } catch (error: any) {
      console.error('❌ Erreur lors de la vérification du token:', error);

      // En mode développement ou réseau désactivé, autoriser le fonctionnement sans token
      if (!isNetworkEnabled()) {
        console.log('🔧 Erreur token ignorée - Réseau désactivé');
        return 'guest_token';
      }

      return null;
    }
  };

  const startTokenCheck = () => {
    tokenCheckInterval.value = setInterval(
      async () => {
        const validToken = await ensureValidToken();
        if (!validToken) {
          console.warn('⚠️ Token invalide, reconnecting...');
          // Le token sera géré dans ensureValidToken
        }
      },
      5 * 60 * 1000 // Vérifier le token toutes les 5 minutes
    );
  };

  const stopTokenCheck = () => {
    if (tokenCheckInterval.value) {
      clearInterval(tokenCheckInterval.value);
      tokenCheckInterval.value = null;
    }
  };

  // Cleanup automatique
  onBeforeUnmount(() => {
    stopTokenCheck();
  });

  return {
    decodeJWT,
    isTokenExpired,
    ensureValidToken,
    startTokenCheck,
    stopTokenCheck,
  };
};
